import React, { useState } from 'react';
import { Box, IconButton, Typography, Button, useMediaQuery, useTheme, keyframes } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CelebrationIcon from '@mui/icons-material/Celebration';
import PaymentIcon from '@mui/icons-material/Payment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useLanguage } from '../context/LanguageContext';

const STORAGE_KEY = 'hidePaymentFixedBanner';

// --- Keyframe Animations ---

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 14px rgba(32, 178, 107, 0.8),
                0 0 28px rgba(157, 232, 189, 0.5),
                0 4px 20px rgba(32, 178, 107, 0.5);
  }
  50% {
    box-shadow: 0 0 22px rgba(32, 178, 107, 1),
                0 0 48px rgba(157, 232, 189, 0.7),
                0 4px 32px rgba(32, 178, 107, 0.7);
  }
`;

const ringPulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 0; }
  100% { transform: scale(1); opacity: 0; }
`;

// --- Component ---

const PaymentFixedBanner = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));

  const [visible, setVisible] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) !== 'true';
  });

  if (!visible) return null;

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
  };

  const handleUpgrade = () => {
    window.location.href = '/dashboard/payment';
  };

  const bannerText = t(
    'paymentFixedBanner',
    '🎉 We now support Lemon Squeezy payments! Upgrade to Pro with a secure one-time payment — no subscription, instant access to all features!'
  );
  const ctaText = t('paymentFixedCTA', 'Upgrade Now');

  // Marquee content — repeated icons + text blocks
  const marqueeContent = (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: matchDownSm ? 3 : 5,
        whiteSpace: 'nowrap',
        px: 2,
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5 }}>
          <CelebrationIcon
            sx={{
              fontSize: 18,
              color: '#ffd700',
              animation: `${pulse} 2s ease-in-out infinite`,
            }}
          />
          <Typography
            component="span"
            sx={{
              fontSize: matchDownSm ? '0.78rem' : '0.85rem',
              fontWeight: 700,
              color: '#fff',
              letterSpacing: '0.01em',
            }}
          >
            {bannerText}
          </Typography>
          <PaymentIcon sx={{ fontSize: 18, color: '#a5f3c4' }} />
        </Box>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 40%, #7c3aed 100%)',
        borderBottom: '2px solid rgba(124, 58, 237, 0.5)',
        boxShadow: '0 4px 20px rgba(124, 58, 237, 0.25)',
        zIndex: 1301,
      }}
    >
      {/* Shimmer overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)',
          backgroundSize: '200% 100%',
          animation: `${shimmer} 3s linear infinite`,
          pointerEvents: 'none',
        }}
      />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: matchDownSm ? 42 : 48,
          py: 0.5,
        }}
      >
        {/* Scrolling marquee with edge fade */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            maskImage:
              'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              animation: `${scroll} 60s linear infinite`,
              '&:hover': {
                animationPlayState: 'paused',
              },
            }}
          >
            {marqueeContent}
            {marqueeContent}
          </Box>
        </Box>

        {/* CTA Button with pulsing ring */}
        <Box sx={{ position: 'relative', mx: matchDownSm ? 0.75 : 1.5, flexShrink: 0 }}>
          {/* Animated expanding ring */}
          <Box
            sx={{
              position: 'absolute',
              inset: -3,
              borderRadius: '999px',
              border: '2px solid rgba(32, 178, 107, 0.5)',
              animation: `${ringPulse} 2s ease-in-out infinite`,
              pointerEvents: 'none',
            }}
          />
          <Button
            onClick={handleUpgrade}
            variant="contained"
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
            sx={{
              position: 'relative',
              px: matchDownSm ? 1.5 : 2.5,
              py: 0.75,
              minWidth: 'auto',
              whiteSpace: 'nowrap',
              borderRadius: '999px',
              fontSize: matchDownSm ? '0.75rem' : '0.82rem',
              fontWeight: 900,
              textTransform: 'none',
              color: '#fff !important',
              background:
                'linear-gradient(135deg, #117A48 0%, #20B26B 50%, #9DE8BD 100%) !important',
              backgroundColor: '#20B26B !important',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              animation: `${glowPulse} 2s ease-in-out infinite`,
              letterSpacing: '0.02em',
              '&:hover': {
                color: '#fff !important',
                background:
                  'linear-gradient(135deg, #0B4F32 0%, #117A48 50%, #20B26B 100%) !important',
                backgroundColor: '#117A48 !important',
                boxShadow:
                  '0 0 24px rgba(32, 178, 107, 0.8), 0 0 48px rgba(32, 178, 107, 0.5), 0 6px 22px rgba(32, 178, 107, 0.5)',
                transform: 'scale(1.08)',
              },
              '&.MuiButton-contained': {
                color: '#fff !important',
                background:
                  'linear-gradient(135deg, #117A48 0%, #20B26B 50%, #9DE8BD 100%) !important',
              },
              transition: 'transform 0.2s ease, background 0.2s ease',
            }}
          >
            {ctaText}
          </Button>
        </Box>

        {/* Close (X) button */}
        <IconButton
          onClick={handleDismiss}
          size="small"
          sx={{
            mr: matchDownSm ? 0.5 : 1,
            color: 'rgba(255, 255, 255, 0.6)',
            flexShrink: 0,
            '&:hover': {
              color: '#fff',
              background: 'rgba(255, 255, 255, 0.12)',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: matchDownSm ? 16 : 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default PaymentFixedBanner;
